import { useQuery } from '@tanstack/react-query'
import { messageService, type Message } from '../services/messageService'
import { useAppStore } from '../stores/useAppStore'

export type NotificationState = 'orange' | 'red' | 'normal'

interface MessageNotificationResult {
  notificationState: NotificationState
  colorClass: string
  bgColorClass: string
  isLoading: boolean
}

export const useMessageNotifications = (): MessageNotificationResult => {
  const { user, selectedDate } = useAppStore()

  // Use the global selected date from the store
  const messageDate = selectedDate
  
  // Validate user and technician ID
  const technicianId = user?.id ? Number(user.id) : null
  const isValidTechnician = technicianId && technicianId > 0
  
  // Fetch messages for the technician using the stored date
  const { data: messages = [], isLoading } = useQuery<Message[]>({
    queryKey: ['technician-messages-notification', technicianId, messageDate],
    queryFn: () => {
      if (!isValidTechnician) {
        return Promise.resolve([])
      }
      return messageService.getMessages(technicianId, messageDate)
    },
    enabled: !!isValidTechnician,
    refetchInterval: 5000, // Refresh every 5 seconds for quicker updates
    staleTime: 1000, // Consider data stale after 1 second for immediate updates
  })
  
  // Determine notification state based on message conditions
  const getNotificationState = (): NotificationState => {
    if (!messages || messages.length === 0) {
      return 'normal'
    }
    
    // Priority 1: Orange - HasBeenUpdated is true and not completed
    if (messages.some(m => m.hasBeenUpdated && !m.completed)) {
      return 'orange'
    }
    
    // Priority 2: Red - Any messages not completed
    if (messages.some(m => !m.completed)) {
      return 'red'
    }
    
    // Normal state - all messages are completed or no concerning conditions
    return 'normal'
  }
  
  const notificationState = getNotificationState()
  
  // Map notification state to CSS classes
  const getColorClasses = () => {
    switch (notificationState) {
      case 'orange':
        return {
          colorClass: 'text-white hover:text-white',
          bgColorClass: 'bg-orange-500 hover:bg-orange-600'
        }
      case 'red':
        return {
          colorClass: 'text-white hover:text-white',
          bgColorClass: 'bg-red-500 hover:bg-red-600'
        }
      case 'normal':
      default:
        return {
          colorClass: '',
          bgColorClass: ''
        }
    }
  }
  
  const { colorClass, bgColorClass } = getColorClasses()
  
  return {
    notificationState,
    colorClass,
    bgColorClass,
    isLoading
  }
}